import { useQuery } from "@tanstack/react-query";

import cookiesUtility from "../utils/cookies.utility"

import api from "./apiClient";

import { IApplicantChance, ICareersData, IMedia, IStudentImmigrationDocuments } from "../interfaces/services";
import { ICombinedApplicantPrograms, ICombinedApplicantScoreHypoGroups, ICombinedApplicantScores, StudentPROptionsCategoryGroup } from "../interfaces/student";
import { StudentOnboardingResponseOptionsField } from "../interfaces";

export const useGetSignedUrl = (filePath: string | null) => {
    const queryFn = async (): Promise<string | null> => {
        if (!filePath) return null;

        const res = await api.get(`/misc/signed-url/asset/${filePath.replaceAll("/", ".slash.")}`);
        return res.data;
    };

    return useQuery({
        queryFn,
        queryKey: ['signed-url', filePath],
        enabled: !!filePath,
        staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
        gcTime: 30 * 24 * 60 * 60 * 1000, // Keep in cache for 30 days
    });
};

export const useGetSVGContent = (filePath: string | null) => {
    const queryFn = async (): Promise<string | null> => {
        if (!filePath) return null;

        const res = await api.get(`/misc/svg-content/${filePath.replaceAll("/", ".slash.")}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['svg-content', filePath],
        queryFn,
        enabled: !!filePath,
        staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
        gcTime: 30 * 24 * 60 * 60 * 1000, // Keep in cache for 30 days
    });
};

const useGetCareersData = (studentId: number) => {
    const queryFn = async (): Promise<ICareersData> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);

        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get(`/misc/careers-data/${studentId}`);
        return response.data;
    };

    return useQuery({
        queryKey: ["careers-data", studentId],
        queryFn,
        enabled: !!studentId && studentId !== 0
    });
};

const useGetCareersMedia = (studentId: number, mediaId: number) => {
    const queryFn = async (): Promise<IMedia> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);

        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get(`/misc/careers-media/${studentId}/${mediaId}`);
        return response.data;
    };

    return useQuery({
        queryKey: ["careers-media", studentId, mediaId],
        queryFn,
        enabled: !!studentId && !!mediaId
    });
};

const useGetApplicantChance = (studentId: number, chanceType: any) => {
    const queryFn = async (): Promise<IApplicantChance> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get(`misc/applicant-chances/${studentId}/${chanceType}`);
        return response.data;
    };

    return useQuery({
        queryKey: ['applicant_chance', studentId, chanceType],
        queryFn,
        retry: 0,
        refetchOnWindowFocus: false,
        enabled: !!studentId && !!chanceType
    });
};

const useGetStudentImmigrationDocuments = (studentId: number) => {
    const queryFn = async (): Promise<IStudentImmigrationDocuments> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get(`misc/student-immigration-documents/${studentId}`);
        return response.data.data;
    };

    return useQuery({
        queryKey: ['student_immigration_documents', studentId],
        queryFn,
        enabled: !!studentId
    });
}

const useGetStudentImmigrationDocumentMedia = (studentId: number, documentType: string, documentId: number) => {
    const queryFn = async (): Promise<string> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get(`misc/student-immigration-document-media/${studentId}/${documentType}/${documentId}`);
        return response.data.data;
    }

    return useQuery({
        queryKey: ['student_immigration_document_media', studentId, documentType, documentId],
        queryFn,
        retry: 0,
        enabled: !!studentId && !!documentType && !!documentId
    });
}

const useGetStudentApplicantScores = (studentId: number, linked: boolean) => {
    const queryFn = async (): Promise<ICombinedApplicantScores> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get(`misc/student-applicant-scores/${studentId}`);
        return response.data;
    }

    return useQuery({
        queryKey: ['student_applicant_scores', studentId],
        queryFn,
        enabled: !!studentId && linked
    });
}

const useGetStudentApplicantPrograms = (studentId: number, categoryGroup: StudentPROptionsCategoryGroup, linked: boolean) => {
    const queryFn = async (): Promise<ICombinedApplicantPrograms> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get(`misc/student-applicant-programs/${studentId}/${categoryGroup}`);
        return response.data;
    }

    return useQuery({
        queryKey: ['student_applicant_programs', studentId, categoryGroup],
        queryFn,
        enabled: !!studentId && !!categoryGroup && linked
    });
}

const useGetStudentApplicantScoreHypoGroups = (studentId: number, linked: boolean) => {
    const queryFn = async (): Promise<ICombinedApplicantScoreHypoGroups> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get(`misc/student-applicant-score-hypo-groups/${studentId}`);
        return response.data;
    }

    return useQuery({
        queryKey: ['student_applicant_score_hypo_groups', studentId],
        queryFn,
        enabled: !!studentId && linked
    });
};

const useGetStudentOnboardingResponsesOptionsByField = (field: StudentOnboardingResponseOptionsField) => {
    const queryFn = async (): Promise<string[]> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get(`misc/student-onboarding-responses/options/user/${field}`);
        return response.data;
    }

    return useQuery({
        queryKey: ['student_onboarding_responses_options', field],
        queryFn,
        enabled: !!field
    });
}
export {
    useGetCareersData,
    useGetCareersMedia,
    useGetApplicantChance,
    useGetStudentImmigrationDocuments,
    useGetStudentImmigrationDocumentMedia,
    useGetStudentApplicantScores,
    useGetStudentApplicantPrograms,
    useGetStudentApplicantScoreHypoGroups,
    useGetStudentOnboardingResponsesOptionsByField
};